import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { BulkBar } from "../../../components/utils/BulkBar";
import {
  fetchOutboundCommunications,
  fetchOutboundCommunicationPdf,
  fetchOutboundCommunicationsExportPdf,
  parseOutboundCommunicationPdfError,
} from "../../../api/outbound_communications";

const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const pdfErrorMessage = (payload, fallback) => {
  if (Array.isArray(payload?.errors) && payload.errors[0]) return payload.errors[0];
  return payload?.message || fallback;
};

export const OutboundCommunicationsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get("query") || "");
  const [debouncedQuery, setDebouncedQuery] = useState(
    searchParams.get("query") || ""
  );
  const [exportingId, setExportingId] = useState(null);
  const [exportingBulk, setExportingBulk] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const lastFetchKey = useRef(null);
  const page = Number(searchParams.get("page") || 1);

  const applyDebouncedQuery = useMemo(
    () =>
      debounce((value) => {
        setDebouncedQuery((prev) => (prev === value ? prev : value));
      }, 350),
    []
  );

  useEffect(() => () => applyDebouncedQuery.cancel(), [applyDebouncedQuery]);

  useEffect(() => {
    applyDebouncedQuery(query);
  }, [query, applyDebouncedQuery]);

  useEffect(() => {
    const urlQuery = searchParams.get("query") || "";
    if (debouncedQuery === urlQuery) return;
    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    if (debouncedQuery) next.set("query", debouncedQuery);
    else next.delete("query");
    setSearchParams(next, { replace: true });
  }, [debouncedQuery, searchParams, setSearchParams]);

  useEffect(() => {
    const fetchKey = `${page}|${debouncedQuery || ""}`;
    if (lastFetchKey.current === fetchKey) return undefined;
    lastFetchKey.current = fetchKey;

    let cancelled = false;
    setLoading(true);
    setSelectedIds([]);
    fetchOutboundCommunications({
      page,
      per_page: 25,
      query: debouncedQuery || undefined,
      sortBy: "sent_at",
      sort: "desc",
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res?.data || []);
        setCount(res?.count || 0);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("outbound_communications.load_error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, debouncedQuery, t]);

  const rowIds = useMemo(() => rows.map((row) => row.id).filter(Boolean), [rows]);
  const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;
  const exporting = Boolean(exportingId) || exportingBulk;

  const submitSearch = (event) => {
    event.preventDefault();
    applyDebouncedQuery.cancel();
    if (query === debouncedQuery) return;
    setDebouncedQuery(query);
  };

  const toggleRow = (event, id) => {
    event.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const toggleAll = (event) => {
    event.stopPropagation();
    setSelectedIds(allSelected ? [] : rowIds);
  };

  const handleExportPdf = async (event, id) => {
    event.stopPropagation();
    if (!id || exporting) return;
    setExportingId(id);
    try {
      const res = await fetchOutboundCommunicationPdf(id);
      if (res?.pdf_url) {
        window.open(res.pdf_url, "_blank", "noopener");
        toast.success(t("outbound_communications.export_success"));
      } else {
        toast.error(t("outbound_communications.pdf_failed"));
      }
    } catch (error) {
      const payload = await parseOutboundCommunicationPdfError(error);
      toast.error(pdfErrorMessage(payload, t("outbound_communications.pdf_failed")));
    } finally {
      setExportingId(null);
    }
  };

  const handleBulkExport = async () => {
    if (!selectedIds.length || exporting) return;
    setExportingBulk(true);
    try {
      const res = await fetchOutboundCommunicationsExportPdf(selectedIds);
      if (res?.pdf_url) {
        window.open(res.pdf_url, "_blank", "noopener");
        toast.success(t("outbound_communications.export_success"));
      } else {
        toast.error(t("outbound_communications.pdf_failed"));
      }
    } catch (error) {
      const payload = await parseOutboundCommunicationPdfError(error);
      toast.error(pdfErrorMessage(payload, t("outbound_communications.pdf_failed")));
    } finally {
      setExportingBulk(false);
    }
  };

  return (
    <DashboardHeader
      title={t("outbound_communications.title")}
      variant="list"
      listCount={count}
    >
      <div className="px-4 py-4 sm:px-6">
        <form onSubmit={submitSearch} className="mb-4 flex gap-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("outbound_communications.search_placeholder")}
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
          />
          <button
            type="submit"
            className="rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white"
          >
            {t("outbound_communications.search")}
          </button>
        </form>

        {loading ? (
          <ResourceLoading />
        ) : rows.length === 0 ? (
          <ResourceEmpty />
        ) : (
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={toggleAll}
                      aria-label={t("outbound_communications.select_all")}
                    />
                  </th>
                  <th className="px-4 py-3">{t("outbound_communications.col_sent")}</th>
                  <th className="px-4 py-3">{t("outbound_communications.col_subject")}</th>
                  <th className="px-4 py-3">{t("outbound_communications.col_from")}</th>
                  <th className="px-4 py-3">{t("outbound_communications.col_to")}</th>
                  <th className="px-4 py-3">{t("outbound_communications.col_sent_by")}</th>
                  <th className="px-4 py-3 text-right">
                    {t("outbound_communications.export_pdf")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                    onClick={() => navigate(`/dashboard/communications/${row.id}`)}
                  >
                    <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={(event) => toggleRow(event, row.id)}
                        aria-label={row.subject || t("outbound_communications.no_subject")}
                      />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                      {formatWhen(row.sent_at)}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {row.subject || t("outbound_communications.no_subject")}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.from_address || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {(row.to_addresses || []).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.sent_by_user?.displayTitle ||
                        row.sent_by_user?.name ||
                        t("outbound_communications.system")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-slate-50 disabled:opacity-60"
                        onClick={(event) => handleExportPdf(event, row.id)}
                        disabled={exporting}
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" aria-hidden />
                        {exportingId === row.id
                          ? t("outbound_communications.pdf_loading")
                          : t("outbound_communications.export_pdf")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <BulkBar
        count={selectedIds.length}
        onClear={() => setSelectedIds([])}
        actions={[
          {
            label: exportingBulk
              ? t("outbound_communications.pdf_loading")
              : t("outbound_communications.export_pdf"),
            icon: DocumentArrowDownIcon,
            action: handleBulkExport,
            disabled: exporting,
          },
        ]}
      />
    </DashboardHeader>
  );
};
